import React, { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";

/**
 * Find "Channel Allocation Table" or similar budget table and parse into { baseBudget, headers, rows }.
 */
function parseChannelAllocationTable(text) {
  if (!text || typeof text !== "string") return null;
  const lower = text.toLowerCase();
  const idx = lower.indexOf("channel allocation table");
  if (idx === -1) return null;

  const baseBudgetMatch = text.slice(idx).match(/base budget\s*=\s*\$?\s*([\d,]+)/i);
  const baseBudget = baseBudgetMatch ? baseBudgetMatch[1].replace(/,/g, "") : null;

  const fromStart = text.slice(idx);
  const lines = fromStart.split(/\r?\n/);
  let tableStart = -1;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/^\s*\|.+\|/.test(line) && line.includes("|") && !/^[\s|:-]+$/.test(line.trim().replace(/\|/g, ""))) {
      tableStart = i;
      break;
    }
  }
  if (tableStart === -1) return { baseBudget, before: null, after: null, headers: [], rows: [] };

  const parseRow = (line) => {
    const cells = line.split("|").map((s) => s.trim()).filter((s, i, arr) => {
      if (i === 0 && arr.length > 1 && s === "") return false;
      if (i === arr.length - 1 && s === "") return false;
      return true;
    });
    return cells.filter((c) => c !== "");
  };

  const headers = parseRow(lines[tableStart]);
  if (headers.length === 0) return { baseBudget, before: null, after: null, headers: [], rows: [] };

  const rows = [];
  for (let i = tableStart + 1; i < lines.length; i++) {
    const line = lines[i];
    if (!/^\s*\|/.test(line)) break;
    const trimmed = line.trim();
    if (/^[\s|:-]+$/.test(trimmed.replace(/\s/g, ""))) continue;
    const cells = parseRow(line);
    const hasContent = cells.some((c) => c && !/^[\s\-:|]+$/.test(c));
    if (cells.length > 0 && hasContent) rows.push(cells);
  }

  const before = (text.slice(0, idx) + lines.slice(0, tableStart).join("\n")).trim();
  const afterStart = idx + lines.slice(0, tableStart + 1 + rows.length).join("\n").length;
  let after = text.slice(afterStart).trim();
  const afterLines = after.split(/\r?\n/);
  const firstNonTableLine = afterLines.findIndex((l) => l.trim() !== "" && !/^\s*\|/.test(l));
  after = firstNonTableLine >= 0 ? afterLines.slice(firstNonTableLine).join("\n").trim() : "";

  return { baseBudget, before, after, headers, rows };
}

function renderCellContent(str) {
  if (!str || typeof str !== "string") return str;
  const parts = str.split(/\*\*/);
  return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

function parseTableRow(line) {
  const cells = line.split("|").map((s) => s.trim()).filter((s, i, arr) => {
    if (i === 0 && arr.length > 1 && s === "") return false;
    if (i === arr.length - 1 && s === "") return false;
    return true;
  });
  return cells.filter((c) => c !== "");
}

function parseAllMarkdownTables(text) {
  if (!text || typeof text !== "string") return [{ type: "markdown", text }];
  const lines = text.split(/\r?\n/);
  const segments = [];
  let pos = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const looksLikeHeader = /^\s*\|.+\|/.test(line) && !/^[\s|:-]+$/.test(line.trim().replace(/\|/g, ""));
    if (!looksLikeHeader) continue;

    const headers = parseTableRow(line);
    if (headers.length === 0) continue;

    const rows = [];
    let j = i + 1;
    while (j < lines.length && /^\s*\|/.test(lines[j])) {
      const trimmed = lines[j].trim();
      if (!/^[\s|:-]+$/.test(trimmed.replace(/\s/g, ""))) {
        const cells = parseTableRow(lines[j]);
        if (cells.some((c) => c && !/^[\s\-:|]+$/.test(c))) rows.push(cells);
      }
      j++;
    }

    if (rows.length > 0) {
      if (i > pos) {
        const markdownText = lines.slice(pos, i).join("\n").trim();
        if (markdownText) segments.push({ type: "markdown", text: markdownText });
      }
      segments.push({ type: "table", headers, rows });
      pos = j;
      i = j - 1;
    }
  }
  if (pos < lines.length) {
    const rest = lines.slice(pos).join("\n").trim();
    const restLines = rest.split(/\r?\n/);
    const firstNonTable = restLines.findIndex((l) => l.trim() !== "" && !/^\s*\|/.test(l));
    const trimmedRest = firstNonTable >= 0 ? restLines.slice(firstNonTable).join("\n").trim() : "";
    if (trimmedRest) segments.push({ type: "markdown", text: trimmedRest });
  }
  return segments.length > 0 ? segments : [{ type: "markdown", text }];
}

function RenderedTable({ headers, rows }) {
  const colCount = Math.max(headers.length, ...rows.map((r) => r.length));
  return (
    <div className="report-display__table-wrapper">
      <table className="report-display__table">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} className={`report-display__th ${i === 1 || i === 2 ? "report-display__th--right" : "report-display__th--left"}`}>
                {renderCellContent(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((cells, rowIdx) => (
            <tr key={rowIdx} className={cells[0] && cells[0].toLowerCase() === "total" ? "report-display__tr-total" : ""}>
              {cells.map((cell, cellIdx) => (
                <td key={cellIdx} className={`report-display__td ${cellIdx === 1 || cellIdx === 2 ? "report-display__td--right" : "report-display__td--left"}`}>
                  {renderCellContent(cell)}
                </td>
              ))}
              {Array.from({ length: colCount - cells.length }).map((_, i) => (
                <td key={`e-${i}`} className="report-display__td" />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CampaignPlanContent({ content }) {
  const segments = useMemo(() => parseAllMarkdownTables(content), [content]);

  if (!segments || segments.length === 0) {
    return <ReactMarkdown>{content}</ReactMarkdown>;
  }
  if (segments.length === 1 && segments[0].type === "markdown") {
    return <ReactMarkdown>{segments[0].text}</ReactMarkdown>;
  }

  return (
    <>
      {segments.map((seg, idx) =>
        seg.type === "markdown" ? (
          <ReactMarkdown key={idx}>{seg.text}</ReactMarkdown>
        ) : (
          <RenderedTable key={idx} headers={seg.headers} rows={seg.rows} />
        )
      )}
    </>
  );
}

function MarketingPlanContent({ content }) {
  const parsed = useMemo(() => parseChannelAllocationTable(content), [content]);

  if (!parsed || parsed.headers.length === 0 || parsed.rows.length === 0) {
    return <ReactMarkdown>{content}</ReactMarkdown>;
  }

  const { before, after, headers, rows } = parsed;
  const colCount = Math.max(headers.length, ...rows.map((r) => r.length));

  return (
    <>
      {before && (
        <div className="markdown-before-channel-table">
          <ReactMarkdown>{before}</ReactMarkdown>
        </div>
      )}
      <div className="channel-allocation-table-wrapper">
        <div className="report-display__table-wrapper">
          <table className="report-display__table">
            <thead>
              <tr>
                {headers.map((h, i) => (
                  <th key={i} className={`report-display__th ${i === 1 || i === 2 ? "report-display__th--right" : "report-display__th--left"}`}>
                    {renderCellContent(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((cells, rowIdx) => (
                <tr key={rowIdx} className={cells[0] && cells[0].toLowerCase() === "total" ? "report-display__tr-total" : ""}>
                  {cells.map((cell, cellIdx) => (
                    <td key={cellIdx} className={`report-display__td ${cellIdx === 1 || cellIdx === 2 ? "report-display__td--right" : "report-display__td--left"}`}>
                      {renderCellContent(cell)}
                    </td>
                  ))}
                  {Array.from({ length: colCount - cells.length }).map((_, i) => (
                    <td key={`empty-${i}`} className="report-display__td" />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {after && (
        <div className="markdown-after-channel-table">
          <ReactMarkdown>{after}</ReactMarkdown>
        </div>
      )}
    </>
  );
}

const TABS = [
  { id: "marketing", label: "Marketing Plan" },
  { id: "campaign", label: "Google Ads Strategy" },
  { id: "json", label: "Structured Data" },
];

const STAGE_LABELS = {
  stage1: "Stage 1: Marketing Plan",
  stage2: "Stage 2: Campaign Plan",
  stage3a: "Stage 3a: Structure",
  stage3b: "Stage 3b: Explanations",
  stage4: "Stage 4: Payload Conversion",
};

const formatNumber = (n) => (n || 0).toLocaleString();

const ReportDisplay = ({ reportId, onClose, isAdmin = false }) => {
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState("marketing");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTokenBreakdown, setShowTokenBreakdown] = useState(false);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (reportId) {
      fetchReport();
    }
  }, [reportId]);

  const handleRetry = async (fromStage) => {
    setRetrying(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
      const response = await fetch(`/api/google_ads_ai/reports/${reportId}/retry_generation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(csrfToken && { "X-CSRF-Token": csrfToken }),
        },
        body: JSON.stringify({ from_stage: fromStage }),
      });
      if (response.ok) {
        setReport((prev) => ({ ...prev, status: "pending", error_message: null }));
        fetchReport();
        if (window.__googleAdsGenerationQueueRefresh) window.__googleAdsGenerationQueueRefresh();
      } else {
        const data = await response.json();
        setError(data.error || "Retry failed");
      }
    } catch (err) {
      setError("Error retrying: " + err.message);
    } finally {
      setRetrying(false);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/google_ads_ai/reports/${reportId}`, {
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        const data = await response.json();
        setReport(data);
      } else {
        setError("Failed to load report");
      }
    } catch (err) {
      setError("Error loading report: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { modifier: "pending", label: "Pending" },
      stage1_in_progress: { modifier: "in-progress", label: "Stage 1: Marketing Plan" },
      stage2_in_progress: { modifier: "in-progress", label: "Stage 2: Campaign Plan" },
      stage3_in_progress: { modifier: "in-progress", label: "Stage 3a: Structure" },
      stage3b_in_progress: { modifier: "in-progress", label: "Stage 3b: Explanations" },
      stage4_in_progress: { modifier: "in-progress", label: "Stage 4: Conversion" },
      completed: { modifier: "completed", label: "Completed" },
      failed: { modifier: "failed", label: "Failed" },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className={`report-display__status-badge report-display__status-badge--${config.modifier}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="report-display">
        <div className="report-display__loading">Loading report...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-display">
        <div className="report-display__error-state">{error}</div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <div className="report-display">
      <div className="report-display__header">
        <div>
          <h3 className="report-display__title">AI Generation Report</h3>
          <div className="report-display__meta">
            <span className="report-display__event-name">{report.event_name || "Unknown Event"}</span>
            {getStatusBadge(report.status)}
          </div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose} className="report-display__close-btn">
            ×
          </button>
        )}
      </div>

      <div className="report-display__tabs">
        {TABS.map((tab) => {
          const isDisabled =
            (tab.id === "marketing" && !report.marketing_plan) ||
            (tab.id === "campaign" && !report.campaign_plan) ||
            (tab.id === "json" && !report.structured_payload);
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              disabled={isDisabled}
              className={`report-display__tab ${activeTab === tab.id ? "report-display__tab--active" : ""} ${isDisabled ? "report-display__tab--disabled" : ""}`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="report-display__content">
        {activeTab === "marketing" &&
          (report.marketing_plan ? (
            <div className="markdown-content report-display__markdown">
              <MarketingPlanContent content={report.marketing_plan} />
            </div>
          ) : (
            <EmptyState message="Marketing plan not yet generated." />
          ))}

        {activeTab === "campaign" &&
          (report.campaign_plan ? (
            <div className="markdown-content report-display__markdown">
              <CampaignPlanContent content={report.campaign_plan} />
            </div>
          ) : (
            <EmptyState message="Campaign plan not yet generated." />
          ))}

        {activeTab === "json" &&
          (report.structured_payload ? (
            <pre className="report-display__json-pre">{JSON.stringify(report.structured_payload, null, 2)}</pre>
          ) : (
            <EmptyState message="Structured payload not yet generated." />
          ))}
      </div>

      {report.error_message && (
        <div className="report-display__error-box">
          <strong>Error:</strong> {report.error_message}
          {report.status === "failed" && report.resumable_stage && (
            <div className="report-display__retry">
              <button
                type="button"
                className="report-display__retry-btn"
                onClick={() => handleRetry(report.resumable_stage)}
                disabled={retrying}
              >
                {retrying ? "Retrying..." : `Retry from ${report.resumable_stage_label || report.resumable_stage}`}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="report-display__footer">
        <div className="report-display__footer-row">
          <span>Report ID: {report.id}</span>
          <span>Generated: {report.created_at && new Date(report.created_at).toLocaleString()}</span>
          {isAdmin && report.total_token_usage?.total > 0 && (
            <span className="report-display__token-summary">
              Tokens<span className="admin-only-dot" />: {formatNumber(report.total_token_usage.total)}
              {report.estimated_cost && ` | Est. cost: ${report.estimated_cost}`}
              <button
                type="button"
                className="report-display__token-toggle"
                onClick={() => setShowTokenBreakdown((prev) => !prev)}
                title="Toggle token breakdown by stage"
              >
                {showTokenBreakdown ? "▲" : "▼"}
              </button>
            </span>
          )}
        </div>
        {isAdmin && showTokenBreakdown && report.token_usage && Object.keys(report.token_usage).length > 0 && (
          <div className="report-display__token-breakdown">
            <table className="report-display__token-table">
              <thead>
                <tr>
                  <th>Stage</th>
                  <th>Input</th>
                  <th>Cached</th>
                  <th>Reasoning</th>
                  <th>Output</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(report.token_usage).map(([stage, usage]) => (
                  <tr key={stage}>
                    <td>{STAGE_LABELS[stage] || stage}</td>
                    <td>{formatNumber(usage.input)}</td>
                    <td>{formatNumber(usage.cached)}</td>
                    <td>{formatNumber(usage.reasoning)}</td>
                    <td>{formatNumber(usage.output)}</td>
                    <td><strong>{formatNumber(usage.total)}</strong></td>
                  </tr>
                ))}
                {report.total_token_usage && (
                  <tr className="report-display__token-total-row">
                    <td><strong>Total</strong></td>
                    <td><strong>{formatNumber(report.total_token_usage.input)}</strong></td>
                    <td><strong>{formatNumber(report.total_token_usage.cached)}</strong></td>
                    <td><strong>{formatNumber(report.total_token_usage.reasoning)}</strong></td>
                    <td><strong>{formatNumber(report.total_token_usage.output)}</strong></td>
                    <td><strong>{formatNumber(report.total_token_usage.total)}</strong></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="report-display__empty-state">
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="report-display__empty-icon">
      <path
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
    <p>{message}</p>
  </div>
);

export default ReportDisplay;
